import type { Middleware, UnknownAction } from '@reduxjs/toolkit';
import { TagTypes } from '../../Api/Types/tagTypes';
import { bookingsApi } from '../../Features/Bookings';
import { reportsApi } from '../../Features/Reports';

type ActionWithEndpointMeta = UnknownAction & {
  meta?: { arg?: { endpointName?: string } };
};

const REPORT_AFFECTING_BOOKING_MUTATIONS: readonly string[] = [
  'cancelBooking',
  'settleBookingForAccounting',
  'unsettleBookingForAccounting',
];

// Some of these endpoints are injected onto `bookingsApi` by modules whose import order isn't
// guaranteed here, so match on the action shape RTK Query produces
// (`{reducerPath}/executeMutation/fulfilled` + `meta.arg.endpointName`) instead of `matchFulfilled`.
const isReportAffectingBookingMutationFulfilled = (action: UnknownAction): boolean => {
  const endpointName = (action as ActionWithEndpointMeta).meta?.arg?.endpointName;
  return (
    action.type === `${bookingsApi.reducerPath}/executeMutation/fulfilled` &&
    endpointName !== undefined &&
    REPORT_AFFECTING_BOOKING_MUTATIONS.includes(endpointName)
  );
};

/**
 * Composition-root cache bridge from booking mutations to the accounting reports.
 *
 * RTK Query tags are scoped per `createApi`: `bookingsApi` tags never reach the cache of the
 * separate `reportsApi` slice. Cancelling or (un)settling a booking changes what reports such as
 * "Nierozliczone bookingi" return, so this middleware invalidates `ReportsList` in `reportsApi`
 * after each of those mutations fulfils. Rejected mutations leave the report cache untouched.
 */
export const bookingReportsCacheListener: Middleware = (storeApi) => (next) => (action) => {
  const result = next(action);

  if (isReportAffectingBookingMutationFulfilled(action as UnknownAction)) {
    storeApi.dispatch(reportsApi.util.invalidateTags([TagTypes.ReportsList]));
  }

  return result;
};
